import type { ApolloError } from '@apollo/client';

import {
  Actions as ResumeActions,
  type ResumeAction,
} from 'src/components/Profile/Resume/reducer';
import { FileStatus } from 'src/components/Profile/Resume/types';
import { RESUME } from 'src/components/Router';
import { UPDATE_ROUTE } from 'src/components/Router/reducer';
import { EDIT } from 'src/consts';
import { isGraphQLAuthError } from 'src/modules/error-handling/error-handling';
import type { ReduxInjections, Action, Mode } from 'src/types';

import {
  CreateResumeActions,
  type CreateStep,
  type CreateResumeAction,
} from './reducer';
import type { CreateResumeRoute } from './types';

export const updateActiveRoute = ({
  route,
  source,
}: {
  route: CreateResumeRoute;
  source: string;
}): CreateResumeAction => ({
  type: CreateResumeActions.UPDATE_ACTIVE_ROUTE,
  payload: route,
  meta: {
    metrics: {
      createResumeRoute: route,
      createResumeRouteChangeSource: source,
    },
  },
});

export const setRouteAsTouched = (
  name: CreateResumeRoute,
): CreateResumeAction => ({
  type: CreateResumeActions.SET_ROUTE_AS_TOUCHED,
  payload: name,
  meta: {
    metrics: {
      createResumeRoute: name,
    },
  },
});

export const setAllRoutesAsTouched = (): CreateResumeAction => ({
  type: CreateResumeActions.SET_ALL_ROUTES_AS_TOUCHED,
});

export const updateCreateStep =
  (newCreateStep: CreateStep) =>
  ({ dispatch, getState }: ReduxInjections<CreateResumeAction>) => {
    const {
      createResume: { createStep: currentCreateStep },
    } = getState();
    return dispatch({
      type: CreateResumeActions.UPDATE_CREATE_STEP,
      payload: { from: currentCreateStep, to: newCreateStep },
      meta: {
        metrics: {
          createResumePage: newCreateStep,
        },
      },
    });
  };

export const createResumeRendered = (): CreateResumeAction => ({
  type: CreateResumeActions.CREATE_RESUME_RENDERED,
});

export const returnToNonProfilePage = (): CreateResumeAction => ({
  type: CreateResumeActions.RETURN_TO_NON_PROFILE_PAGE,
});

export const generateResume = ({
  isDefault,
}: {
  isDefault: boolean;
}): ResumeAction => ({
  type: ResumeActions.RESUME_CREATE_START,
  payload: { isDefault },
  meta: {
    metrics: {
      dragAndDrop: false,
    },
  },
});

type GenerateAction =
  | (Action<typeof UPDATE_ROUTE> & {
      payload: {
        to: string;
        mode: Mode;
        analytics: {
          disabled: boolean;
        };
        params: {
          focusOn: string;
        };
      };
    })
  | (Action<ResumeActions.RESUME_CREATE_SUCCESS> & {
      payload: {
        newResumeId: string;
        isDefault: boolean;
      };
    });

export const generateResumeSuccess =
  ({ newResumeId, isDefault }: { newResumeId: string; isDefault: boolean }) =>
  ({ dispatch }: ReduxInjections<GenerateAction>) => {
    setTimeout(() => {
      dispatch({
        type: UPDATE_ROUTE,
        payload: {
          to: RESUME,
          mode: EDIT,
          analytics: {
            disabled: true,
          },
          params: {
            focusOn: 'manage-resumes',
          },
        },
      });
    }, 1500);

    dispatch({
      type: ResumeActions.RESUME_CREATE_SUCCESS,
      payload: { newResumeId, isDefault },
      meta: {
        metrics: {
          'profile-update': true,
        },
      },
    });
  };

export const generateResumeFailure = (error: ApolloError) => ({
  type: ResumeActions.RESUME_CREATE_FAILURE,
  payload: { error, fileStatus: FileStatus.FILE_EXPORT_ERROR },
  meta: {
    metrics: {
      dragAndDrop: false,
      error: !isGraphQLAuthError(error),
    },
  },
});
